import React, { useContext, createContext, useEffect, useCallback, useState, useRef } from 'react';
import appBranding from '@happylife-a/branding';
import utils from '@happylife-a/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetCartItemsIds,
  useShoppingCarts as useCoreShoppingCarts,
} from '../core/hooks/shoppingCartHooks';
import {
  useGetCartItemsIdsV2,
  useShoppingCartsV2 as useCoreShoppingCartsV2,
  REACT_QUERY_GET_CART_ITEMS_IDS_V2_QUERY_KEY,
  REACT_QUERY_GET_CART_ITEMS_V2_QUERY_KEY,
} from '../core/hooks/v2/shoppingCartHooksV2';
import { reduceCartsTotal } from '../helpers';
import { useUser } from './UserContext';
import { useIncreasedPrices } from '../hooks';

export const ShoppingCartContext = createContext({
  isCostCalculating: false,
  carts: {},
  total: 0,
  toggleCart: () => {},
  isInCart: () => {},
  onCartOpen: () => {},
  updateCartCount: () => {},
  bulkDeleteCarts: () => {},
  refetchCarts: () => {},
  clearCarts: () => {},
  setCartItemsTotal: () => {},
});

export function useShoppingCarts() {
  const context = useContext(ShoppingCartContext);
  if (!context) {
    throw new Error('Please use hook inside of ShoppingCartProvider.');
  }

  return context;
}

export function ShoppingCartProvider({ children }) {
  const isV2ApiEnabled = appBranding.tools.features.isEnabled('UsesV2ApiFlow');
  const { isLoading: isLoadingUser, isUnknownUser, user } = useUser();
  const { increase } = useIncreasedPrices();

  // V1 cart IDs
  const { data: v1Carts, refetch: loadV1CartIds } = useGetCartItemsIds({
    enabled: false,
  });

  // V2 cart IDs
  const { data: v2CartsRaw, refetch: loadV2CartIds } = useGetCartItemsIdsV2({
    enabled: false,
  });

  // Normalize V2 IDs: alias productVariantId → productDetailId for downstream consumers
  const v2Carts = React.useMemo(() => {
    if (!v2CartsRaw?.ids) {
      return v2CartsRaw;
    }
    return {
      ...v2CartsRaw,
      ids: v2CartsRaw.ids.map((item) => ({
        ...item,
        productDetailId: item.productVariantId,
      })),
    };
  }, [v2CartsRaw]);

  const carts = isV2ApiEnabled ? v2Carts : v1Carts;
  const loadShoppingCartIds = isV2ApiEnabled ? loadV2CartIds : loadV1CartIds;

  const queryClient = useQueryClient();

  const clearCarts = useCallback(() => {
    queryClient.setQueryData([REACT_QUERY_GET_CART_ITEMS_IDS_V2_QUERY_KEY], { ids: [], total: 0 });
    queryClient.setQueryData([REACT_QUERY_GET_CART_ITEMS_V2_QUERY_KEY], {
      shoppingCarts: [],
      meta: { total: 0 },
    });
  }, [queryClient]);

  const [cartItemsTotal, setCartItemsTotal] = useState();
  const [totalCost, totalDiscountedCost, totalProductItemsCount] = cartItemsTotal || [0, 0, 0];

  useEffect(() => {
    if (carts?.ids) {
      const hasPriceData = carts.ids.some(
        (item) => item.productDetail?.price || item.productVariant?.price
      );

      if (hasPriceData) {
        setCartItemsTotal(reduceCartsTotal(carts.ids, increase));
      } else {
        // IDs don't carry price data (V2) — only update the item count,
        // preserve any price totals already set by useGetCartItems.
        const count = carts.ids.reduce((acc, item) => acc + (item.quantity || 0), 0);
        setCartItemsTotal((prev) => {
          const [prevCost, prevDiscounted] = prev || [0, 0, 0];
          return [prevCost, prevDiscounted, count];
        });
      }
    }
  }, [carts, increase]);

  // V1 isInCart
  const isInCartV1 = useCallback(
    (productDetailId) => {
      return (
        carts?.ids?.findIndex((id) => utils.helpers.id.same(id.productDetailId, productDetailId)) >
        -1
      );
    },
    [carts]
  );

  // V2 isInCart — accepts productVariantId but checks against the aliased productDetailId
  const refCarts = useRef();
  refCarts.current = carts;

  const isInCartV2 = useCallback((productVariantId) => {
    return (
      refCarts.current?.ids?.findIndex((id) =>
        utils.helpers.id.same(id.productVariantId, productVariantId)
      ) > -1
    );
  }, []);

  const isInCart = isV2ApiEnabled ? isInCartV2 : isInCartV1;

  const [isCostCalculating, setIsCostCalculating] = useState(false);

  const v1Actions = useCoreShoppingCarts(isInCartV1, {
    setIsCostCalculating: setIsCostCalculating,
  });

  const v2Actions = useCoreShoppingCartsV2(isInCartV2, {
    setIsCostCalculating: setIsCostCalculating,
  });

  const { toggleCart, updateCartCount, bulkDeleteCarts } = isV2ApiEnabled ? v2Actions : v1Actions;

  useEffect(() => {
    if (isUnknownUser || isLoadingUser || !user?.id) {
      return;
    }

    loadShoppingCartIds();
  }, [isUnknownUser, isLoadingUser, user?.id, isV2ApiEnabled]);

  return (
    <ShoppingCartContext.Provider
      value={{
        carts: carts,
        isInCart: isInCart,
        isCostCalculating: isCostCalculating,
        totalCost: totalCost,
        totalDiscountedCost: totalDiscountedCost,
        totalProductItemsCount: totalProductItemsCount,
        toggleCart: toggleCart,
        updateCartCount: updateCartCount,
        bulkDeleteCarts: bulkDeleteCarts,
        refetchCarts: loadShoppingCartIds,
        clearCarts: isV2ApiEnabled ? clearCarts : loadShoppingCartIds,
        setCartItemsTotal: setCartItemsTotal,
      }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
}
